import React, { type SyntheticEvent, useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { FormHolidayValue } from '../../../../../../common/types';
import { useSLAConfigActionsFlags } from '../../../../../../common/ui/sla-config-action-flags';
import { useParseIcsService } from '../../../../../../services/parse-ics';
import messages from './messages';

type ImportCalendarProps = Intl & {
	onImport: (importedHolidays: FormHolidayValue[]) => void;
};

const ImportCalendarView = ({ intl: { formatMessage }, onImport }: ImportCalendarProps) => {
	const fileUploadInput = useRef<HTMLInputElement>(null);
	const actionFlags = useSLAConfigActionsFlags();
	const flagMap = {
		SUCCESS: actionFlags.calendarImportSuccessFlag,
		FAILURE: actionFlags.calendarImportParserFailureFlag,
		PARTIAL_SUCCESS: actionFlags.calendarImportPartialFailureFlag,
	};
	const { baseUrl } = useCommonPageContext();
	// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
	const [{ loading }, { parseIcsFile }] = useParseIcsService(baseUrl);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const browse = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'importIcs');
		fileUploadInput.current?.click();
	};
	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files) {
			return;
		}
		const file = event.target.files[0];
		const response = await parseIcsFile(file);
		if (!response) {
			actionFlags.calendarImportParserServerFailureFlag();
			fireTrackAnalytics(createAnalyticsEvent({}), 'icsFile parsed', 'editCalendar', {
				status: 'SERVER_FAILURE',
			});
			return;
		}

		flagMap[response.status]();
		fireTrackAnalytics(createAnalyticsEvent({}), 'icsFile parsed', 'editCalendar', {
			status: response.status,
		});
		onImport(
			response.holidays.map((holiday, idx) => ({
				isRecurring: holiday.recurring,
				name: holiday.name,
				iso8601Date: holiday.iso8601Date,
				isNew: false,
				fieldId: idx,
			})),
		);
	};

	return (
		<>
			<LoadingButton appearance="link" onClick={browse} isLoading={loading}>
				{formatMessage(messages.importHolidayCalendar)}
			</LoadingButton>
			<input type="file" hidden ref={fileUploadInput} onChange={handleFileChange} accept=".ics" />
		</>
	);
};

export const ImportCalendar = injectIntl(ImportCalendarView);
